<template>
  <div>
    <ValidationProvider
      tag="div"
      :rules="{ required: required }"
      :skipIfEmpty="false"
      v-slot="{ validate, errors }"
    >
      <input
        type="hidden"
        name="stripePaymentElementFilled"
        @change="validate"
        v-model="paymentElementFilled"
      />
      <SimpleErrorLine
        v-if="errors.length > 0"
        :message="$t('payment_method.card_payment.validations.required')"
      ></SimpleErrorLine>
    </ValidationProvider>

    <div ref="stripePaymentElement"></div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { ValidationProvider } from "vee-validate";
import SimpleErrorLine from "@/components/Form/SimpleErrorLine.vue";
import {
  Stripe,
  StripeElements,
  StripePaymentElement,
  StripeElementsOptions,
  StripePaymentElementOptions,
  StripeElementsOptionsMode,
} from "@stripe/stripe-js";
import FormResource from "@/module/Form/Resources/FormResource";
import {
  StripeElementsOptionsModePayment,
  StripeElementsOptionsModeSubscription,
} from "@stripe/stripe-js/dist/stripe-js/elements-group";
const stripeToPaymentMethodTypeMap: { [key: string]: string } = {
  card: "credit card",
  paypal: "paypal",
  apple_pay: "apple pay",
  google_pay: "gpay",
};

export default Vue.extend({
  name: "StripePaymentElement",

  components: {
    ValidationProvider,
    SimpleErrorLine,
  },

  props: {
    required: {
      type: Boolean,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    amount: {
      type: Number,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    stripe: {
      type: Object as PropType<Stripe>,
      required: true,
    },
    appearanceOptions: {
      type: Object as PropType<StripeElementsOptions["appearance"]>,
      default: () => ({
        theme: "flat",
        labels: "floating",
        variables: { colorPrimaryText: "#262626" },
      }),
    },
    mode: {
      type: String as PropType<"payment" | "setup">,
      required: true,
    },
  },

  data() {
    return {
      stripeElements: null as StripeElements | null,
      stripePaymentElement: null as StripePaymentElement | null,
      paymentElementFilled: null as boolean | null,
    };
  },

  async mounted() {
    await this.initializeStripeElements();
  },

  watch: {
    amount() {
      if (this.mode === "payment") {
        this.updateStripeElementsOptions();
      }
    },

    currency() {
      this.updateStripeElementsOptions({ forceRecreateOnError: true });
    },
  },

  methods: {
    async initializeStripeElements() {
      this.$emit("paymentMethodSelected", "credit card");

      const allowedPaymentTypes = [
        "gpay",
        "apple pay",
        "credit card",
        "paypal",
      ];
      if (
        !this.stripe ||
        !this.formResource.payment_methods.some((method) =>
          allowedPaymentTypes.includes(method.payment_type),
        )
      ) {
        console.error(
          "Stripe instance not provided or no allowed payment types",
        );
        return;
      }

      const stripeElementsOptions = {
        mode: this.mode,
        currency: this.currency.toLowerCase(),
        appearance: {
          ...{
            theme: "stripe",
            labels: "floating",
            variables: {
              colorPrimary: "#0570de",
              colorBackground: "#ffffff",
              colorText: "#30313d",
            },
          },
          ...this.appearanceOptions,
        },
      } as StripeElementsOptionsMode;

      if (this.mode === "payment") {
        (
          stripeElementsOptions as
            | StripeElementsOptionsModePayment
            | StripeElementsOptionsModeSubscription
        ).amount = Math.round(this.amount * 100);
        (
          stripeElementsOptions as
            | StripeElementsOptionsModePayment
            | StripeElementsOptionsModeSubscription
        ).setup_future_usage =
          this.formResource.allow_gopay_recurring_payments ||
          this.formResource.allow_new_upsell_process
            ? "off_session"
            : "on_session";
      }

      this.stripeElements = this.stripe.elements(stripeElementsOptions);

      const stripeElementsLayoutType =
        this.formResource.template_settings?.container_settings.payment.layout
          .variant === "tile"
          ? "tabs"
          : "accordion";

      const stripePaymentElementOptions: StripePaymentElementOptions = {
        layout: {
          type: stripeElementsLayoutType,
          defaultCollapsed: false,
          radios: stripeElementsLayoutType === "accordion",
          spacedAccordionItems: stripeElementsLayoutType === "accordion",
        },
        fields: { billingDetails: { address: { country: "never" } } },
        terms: {
          card: "never",
          paypal: "never",
          applePay: "never",
          googlePay: "never",
        },
      };

      this.stripePaymentElement = this.stripeElements.create(
        "payment",
        stripePaymentElementOptions,
      );

      const el = this.$refs.stripePaymentElement as HTMLElement | null;

      if (el) {
        this.stripePaymentElement.mount(el);
      } else {
        console.error("Stripe Payment Element ref is undefined");
      }

      this.$emit("stripeElements", this.stripeElements);

      this.stripePaymentElement.on("change", (event) => {
        this.paymentElementFilled = event.complete ? true : null;

        if (event.value?.type) {
          const stripeType = event.value.type;
          const paymentType =
            stripeToPaymentMethodTypeMap[stripeType] ||
            stripeType ||
            "credit card";
          this.$emit("paymentMethodSelected", paymentType);
        }
      });
    },

    updateStripeElementsOptions(opts: { forceRecreateOnError?: boolean } = {}) {
      if (!this.stripeElements) {
        return;
      }

      try {
        const update: Partial<StripeElementsOptionsMode> = {
          currency: this.currency.toLowerCase(),
        };

        if (this.mode === "payment") {
          (
            update as
              | StripeElementsOptionsModePayment
              | StripeElementsOptionsModeSubscription
          ).amount = Math.round(this.amount * 100);
          (
            update as
              | StripeElementsOptionsModePayment
              | StripeElementsOptionsModeSubscription
          ).setup_future_usage =
            this.formResource.allow_gopay_recurring_payments ||
            this.formResource.allow_new_upsell_process
              ? "off_session"
              : "on_session";
        }

        this.stripeElements.update(update);
      } catch (error: unknown) {
        console.warn("elements.update failed, recreating…", error);
        if (opts.forceRecreateOnError) {
          this.recreateStripeElements();
        }
      }
    },

    async recreateStripeElements() {
      this.destroyStripeElements();
      await this.$nextTick();
      await this.initializeStripeElements();
    },

    destroyStripeElements() {
      if (this.stripePaymentElement) {
        this.stripePaymentElement.unmount();
        this.stripePaymentElement = null;
      }
      this.stripeElements = null;
      this.paymentElementFilled = null;
    },
  },

  beforeDestroy() {
    this.destroyStripeElements();
  },
});
</script>
